import api from './client';
import { Timestamp } from 'firebase/firestore';
import type { Broadcast } from 'types';

function toTimestamp(value: any): Timestamp {
  if (!value) return Timestamp.now();
  if (value instanceof Timestamp) return value;
  if (typeof value === 'string') {
    return Timestamp.fromDate(new Date(value));
  }
  if (value.seconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds || 0);
  }
  return Timestamp.now();
}

function fromTimestamp(ts: Timestamp): string {
  return ts.toDate().toISOString();
}

function apiResponseToBroadcast(response: any): Broadcast {
  return {
    ...response,
    start_date: toTimestamp(response.start_date),
    end_date: toTimestamp(response.end_date),
    created_at: toTimestamp(response.created_at),
    updated_at: toTimestamp(response.updated_at),
  };
}

function broadcastToApiPayload(broadcast: Partial<Broadcast>): any {
  const payload: any = { ...broadcast };
  if (broadcast.start_date) payload.start_date = fromTimestamp(broadcast.start_date);
  if (broadcast.end_date) payload.end_date = fromTimestamp(broadcast.end_date);
  delete payload.created_at;
  delete payload.updated_at;
  return payload;
}

export const broadcastsApi = {
  list: async (params?: { org_id?: string; limit?: number; active_only?: boolean }): Promise<Broadcast[]> => {
    const { data } = await api.get('/broadcasts', { params });
    return (data as any[]).map(apiResponseToBroadcast);
  },

  create: async (broadcast: Omit<Broadcast, 'id' | 'created_at' | 'updated_at'>): Promise<Broadcast> => {
    const payload = broadcastToApiPayload(broadcast as any);
    const { data } = await api.post('/broadcasts', payload);
    return apiResponseToBroadcast(data);
  },

  update: async (broadcastId: string, updates: Partial<Broadcast>): Promise<Broadcast> => {
    const payload = broadcastToApiPayload(updates);
    const { data } = await api.patch(`/broadcasts/${broadcastId}`, payload);
    return apiResponseToBroadcast(data);
  },

  delete: async (broadcastId: string): Promise<void> => {
    await api.delete(`/broadcasts/${broadcastId}`);
  },
};
